import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  orderBy,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { logAuditEvent } from './auditService';
import { hasFeatureAccess, getRolePermissions } from '../utils/accessControl';

const EVENTS_COLLECTION = 'calendarEvents';

// Roles that see the full company calendar (not only events where userId matches).
// Must match Firestore USER_ROLES / legacy aliases — previously only "agent"|"admin"
// caused administrator/owner/inspector users to miss colleagues' bookings.
const STAFF_CALENDAR_ROLES = new Set([
  'agent',
  'admin',
  'administrator',
  'owner',
  'inspector'
]);

/**
 * Role docs often omit feature keys; hasFeatureAccess then returns false and calendar used to throw.
 * Treat calendar as allowed unless viewCalendar is explicitly set to false.
 */
const staffMayLoadFullCompanyCalendar = async (userId, userRole) => {
  const granted = await hasFeatureAccess(userId, userRole, 'viewCalendar');
  if (granted) return true;
  try {
    const rolePermissions = await getRolePermissions(userRole);
    if (rolePermissions?.features && Object.prototype.hasOwnProperty.call(rolePermissions.features, 'viewCalendar')) {
      return rolePermissions.features.viewCalendar === true;
    }
  } catch (e) {
    console.warn('getRolePermissions failed for calendar access:', e);
  }
  return true;
};

const validateUser = () => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }
  return user;
};

const validateEventData = (data) => {
  const requiredFields = ['title', 'date', 'time', 'type'];
  const missingFields = requiredFields.filter(field => !data[field]);
  
  if (missingFields.length > 0) {
    throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
  }
};

// Convert date to Firestore Timestamp
const convertToTimestamp = (date) => {
  if (!date) return null;
  if (date instanceof Date) return Timestamp.fromDate(date);
  if (typeof date?.toDate === 'function') return Timestamp.fromDate(date.toDate());
  if (typeof date === 'string') {
    const d = new Date(date);
    return isNaN(d.getTime()) ? null : Timestamp.fromDate(d);
  }
  if (typeof date === 'object' && typeof date.seconds === 'number') {
    // Plain {seconds, nanoseconds} object — already Firestore-like, reconstruct
    return new Timestamp(date.seconds, date.nanoseconds || 0);
  }
  return null;
};

/**
 * Convert any Firestore Timestamp / Date-like value to a JS Date.
 * Uses duck-typing (toDate() check) instead of instanceof so it works even
 * when the Firestore SDK module is bundled multiple times (singleton mismatch).
 */
const convertFromTimestamp = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp;
  // Duck-type: Firestore Timestamp has a toDate() method
  if (typeof timestamp?.toDate === 'function') return timestamp.toDate();
  // Plain serialised Firestore Timestamp {seconds, nanoseconds}
  if (typeof timestamp === 'object' && typeof timestamp.seconds === 'number') {
    return new Date(timestamp.seconds * 1000 + Math.floor((timestamp.nanoseconds || 0) / 1e6));
  }
  if (typeof timestamp === 'string') {
    const d = new Date(timestamp);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

// Get all calendar events
export const getAllCalendarEvents = async () => {
  try {
    const user = validateUser();
    console.log('Getting calendar events for user:', user.uid);
    
    // Get user role and owned properties
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userData = userDoc.data();
    const userRole = userData?.role?.toLowerCase();

    let events = [];

    if (userRole === 'landlord') {
      // For landlords, get events for their properties (landlordId and/or landlordIds[])
      const propertyIdSet = new Set();
      const byLegacyLandlord = await getDocs(
        query(collection(db, 'properties'), where('landlordId', '==', user.uid))
      );
      byLegacyLandlord.docs.forEach((d) => propertyIdSet.add(d.id));
      try {
        const byLandlordIds = await getDocs(
          query(collection(db, 'properties'), where('landlordIds', 'array-contains', user.uid))
        );
        byLandlordIds.docs.forEach((d) => propertyIdSet.add(d.id));
      } catch (e) {
        console.warn('landlordIds calendar property query skipped:', e);
      }
      const propertyIds = [...propertyIdSet];
      
      if (propertyIds.length > 0) {
        // Get events for landlord's properties and their own events
        const batchSize = 10;
        for (let i = 0; i < propertyIds.length; i += batchSize) {
          const batchIds = propertyIds.slice(i, i + batchSize);
          const batchQuery = query(
            collection(db, EVENTS_COLLECTION),
            where('propertyId', 'in', batchIds)
          );
          const batchSnapshot = await getDocs(batchQuery);
          const batchEvents = batchSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          events = [...events, ...batchEvents];
        }
      }
      
      // Also get personal events for the landlord
      const personalEventsQuery = query(
        collection(db, EVENTS_COLLECTION),
        where('userId', '==', user.uid)
      );
      const personalEventsSnapshot = await getDocs(personalEventsQuery);
      const personalEvents = personalEventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      events = [...events, ...personalEvents];
      
    } else if (STAFF_CALENDAR_ROLES.has(userRole)) {
      const useFullCompanyCalendar = await staffMayLoadFullCompanyCalendar(user.uid, userRole);
      if (useFullCompanyCalendar) {
        try {
          const querySnapshot = await getDocs(
            query(collection(db, EVENTS_COLLECTION), orderBy('date', 'asc'))
          );
          events = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        } catch (orderErr) {
          console.warn('Full calendar orderBy query failed; loading own events only:', orderErr);
          const personalSnapshot = await getDocs(
            query(collection(db, EVENTS_COLLECTION), where('userId', '==', user.uid))
          );
          events = personalSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        }
      } else {
        const personalSnapshot = await getDocs(
          query(collection(db, EVENTS_COLLECTION), where('userId', '==', user.uid))
        );
        events = personalSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }
    } else {
      // For tenants, get their own events and property-related events
      const tenantEventsQuery = query(
        collection(db, EVENTS_COLLECTION),
        where('userId', '==', user.uid)
      );
      const tenantEventsSnapshot = await getDocs(tenantEventsQuery);
      events = tenantEventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    }
    
    console.log('Found events:', events.length);
    
    // Remove duplicates and process events
    const uniqueEvents = events.filter((event, index, self) => 
      index === self.findIndex(e => e.id === event.id)
    );
    
    const formattedEvents = uniqueEvents.map(data => ({
      id: data.id,
      ...data,
      date: convertFromTimestamp(data.date),
      createdAt: data.createdAt ? convertFromTimestamp(data.createdAt) : null,
      updatedAt: data.updatedAt ? convertFromTimestamp(data.updatedAt) : null
    }));

    return formattedEvents.sort((a, b) => new Date(a.date) - new Date(b.date));
  } catch (error) {
    console.error('Error getting calendar events:', error);
    throw error;
  }
};

// Get calendar event by ID
export const getCalendarEventById = async (eventId) => {
  try {
    const user = validateUser();

    const docRef = doc(db, EVENTS_COLLECTION, eventId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Calendar event not found');
    }

    const data = docSnap.data();

    // Get user role for access control
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userData = userDoc.data();
    const userRole = userData?.role?.toLowerCase();

    // Check access permissions
    const hasViewAccess = await hasFeatureAccess(user.uid, userRole, 'viewCalendar');
    if (hasViewAccess) {
      // Users with viewCalendar permission have access to all events
    } else if (userRole === 'landlord') {
      // Landlords can access their own events and property events
      if (data.userId !== user.uid && data.propertyId) {
        const propertyRef = doc(db, 'properties', data.propertyId);
        const propertySnap = await getDoc(propertyRef);
        
        if (!propertySnap.exists()) {
          throw new Error('Property not found');
        }
        
        const propertyData = propertySnap.data();
        if (propertyData.landlordId !== user.uid) {
          throw new Error('Unauthorized access to calendar event');
        }
      }
    } else {
      // For tenants, verify the event belongs to them
      if (data.userId !== user.uid) {
        throw new Error('Unauthorized access to calendar event');
      }
    }

    return {
      id: docSnap.id,
      ...data,
      date: convertFromTimestamp(data.date),
      createdAt: data.createdAt ? convertFromTimestamp(data.createdAt) : null,
      updatedAt: data.updatedAt ? convertFromTimestamp(data.updatedAt) : null
    };
  } catch (error) {
    console.error('Error getting calendar event:', error);
    throw error;
  }
};

// Add new calendar event
export const addCalendarEvent = async (eventData) => {
  try {
    const user = validateUser();
    validateEventData(eventData);

    const eventToAdd = {
      ...eventData,
      userId: user.uid,
      date: convertToTimestamp(eventData.date),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: user.uid
    };

    console.log('Adding calendar event:', eventToAdd);
    const docRef = await addDoc(collection(db, EVENTS_COLLECTION), eventToAdd);

    // Log audit event
    await logAuditEvent({
      eventType: 'calendar',
      description: `Calendar event created: ${eventData.title}`,
      action: 'CREATE',
      collection: EVENTS_COLLECTION,
      documentId: docRef.id,
      eventTitle: eventData.title,
      eventType_calendar: eventData.type,
      propertyId: eventData.propertyId || null
    });

    return docRef.id;
  } catch (error) {
    console.error('Error adding calendar event:', error);
    throw error;
  }
};

// Update calendar event
export const updateCalendarEvent = async (eventId, eventData) => {
  try {
    const user = validateUser();

    // Get current event to check permissions
    const currentEvent = await getCalendarEventById(eventId);
    
    const eventToUpdate = {
      ...eventData,
      date: eventData.date ? convertToTimestamp(eventData.date) : currentEvent.date,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid
    };

    // Remove undefined fields
    Object.keys(eventToUpdate).forEach(key => {
      if (eventToUpdate[key] === undefined) {
        delete eventToUpdate[key];
      }
    });

    console.log('Updating calendar event:', eventId, eventToUpdate);
    const docRef = doc(db, EVENTS_COLLECTION, eventId);
    await updateDoc(docRef, eventToUpdate);

    // Log audit event
    await logAuditEvent({
      eventType: 'calendar',
      description: `Calendar event updated: ${eventData.title || currentEvent.title}`,
      action: 'UPDATE',
      collection: EVENTS_COLLECTION,
      documentId: eventId,
      eventTitle: eventData.title || currentEvent.title,
      eventType_calendar: eventData.type || currentEvent.type,
      propertyId: eventData.propertyId || currentEvent.propertyId || null
    });

    return eventId;
  } catch (error) {
    console.error('Error updating calendar event:', error);
    throw error;
  }
};

// Delete calendar event
export const deleteCalendarEvent = async (eventId) => {
  try {
    const user = validateUser();

    // Get current event to check permissions and for audit log
    const currentEvent = await getCalendarEventById(eventId);

    console.log('Deleting calendar event:', eventId);
    const docRef = doc(db, EVENTS_COLLECTION, eventId);
    await deleteDoc(docRef);

    // Log audit event
    await logAuditEvent({
      eventType: 'calendar',
      description: `Calendar event deleted: ${currentEvent.title}`,
      action: 'DELETE',
      collection: EVENTS_COLLECTION,
      documentId: eventId,
      eventTitle: currentEvent.title,
      eventType_calendar: currentEvent.type,
      propertyId: currentEvent.propertyId || null
    });

    return true;
  } catch (error) {
    console.error('Error deleting calendar event:', error);
    throw error;
  }
};

// Get events for a specific date range
export const getCalendarEventsByDateRange = async (startDate, endDate) => {
  try {
    const user = validateUser();
    console.log('Getting calendar events for date range:', startDate, 'to', endDate);
    
    const startTimestamp = convertToTimestamp(startDate);
    const endTimestamp = convertToTimestamp(endDate);
    
    // Get user role for filtering
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userData = userDoc.data();
    const userRole = userData?.role?.toLowerCase();
    
    let q;
    let events = [];
    
    // Check RBAC permission
    const hasAccess = await hasFeatureAccess(user.uid, userRole, 'viewCalendar');
    if (hasAccess) {
      // For users with permission, get all events in date range
      q = query(
        collection(db, EVENTS_COLLECTION),
        where('date', '>=', startTimestamp),
        where('date', '<=', endTimestamp),
        orderBy('date', 'asc')
      );
      const querySnapshot = await getDocs(q);
      events = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } else {
      // For landlords and tenants, use the existing getAllCalendarEvents and filter
      const allEvents = await getAllCalendarEvents();
      events = allEvents.filter(event => {
        const eventDate = new Date(event.date);
        return eventDate >= new Date(startDate) && eventDate <= new Date(endDate);
      });
    }
    
    return events.map(data => ({
      id: data.id,
      ...data,
      date: convertFromTimestamp(data.date) || data.date,
      createdAt: data.createdAt ? (convertFromTimestamp(data.createdAt) || data.createdAt) : null,
      updatedAt: data.updatedAt ? (convertFromTimestamp(data.updatedAt) || data.updatedAt) : null
    }));
  } catch (error) {
    console.error('Error getting calendar events by date range:', error);
    throw error;
  }
};

// Get upcoming events (next 30 days)
export const getUpcomingCalendarEvents = async (limit = 10) => {
  try {
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    
    const events = await getCalendarEventsByDateRange(today, thirtyDaysFromNow);
    return events.slice(0, limit);
  } catch (error) {
    console.error('Error getting upcoming calendar events:', error);
    throw error;
  }
}; 